import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle, Upload, FileText, AlertCircle, ArrowLeft, ChevronRight,
} from "lucide-react";

/* ─── TOKENS ─── */
const C = {
  white:   "#FFFFFF",
  offWhite:"#F8F7F5",
  cream:   "#F2F0EC",
  black:   "#0A0A0A",
  dark:    "#1A1A1A",
  gray:    "#6B6B6B",
  grayLt:  "#D4D4D4",
  border:  "#E5E3DF",
  red:     "#E50914",
  redDark: "#B00710",
  redLt:   "#FFF0F0",
  sage:    "#4A7C59",
  sageLt:  "#F0F6F2",
};

/* ─── Font ─── */
const fontHref = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

const API = "https://europe-west3-forevagroup.cloudfunctions.net/ContactUs";

/* ════════════════════════════════════
   APPLICATION FORM
   ════════════════════════════════════ */
export default function Application() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const role      = (location.state as any)?.role;

  const [form, setForm] = useState({
    firstName:   "",
    lastName:    "",
    email:       "",
    phone:       "",
    linkedin:    "",
    portfolio:   "",
    experience:  "",
    whyUs:       "",
    salary:      "",
    startDate:   "",
    noticePeriod:"",
  });
  const [cv,       setCv]       = useState<File | null>(null);
  const [cover,    setCover]    = useState<File | null>(null);
  const [focus,    setFocus]    = useState<string | null>(null);
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<"cv" | "cover" | null>(null);

  const cvRef    = useRef<HTMLInputElement>(null);
  const coverRef = useRef<HTMLInputElement>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const inp = (field: string): React.CSSProperties => ({
    width: "100%", boxSizing: "border-box" as const,
    background: C.white,
    border: `1.5px solid ${focus === field ? C.red : C.border}`,
    padding: "13px 16px",
    color: C.black,
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    borderRadius: 0,
    transition: "border-color 0.2s",
    marginBottom: 0,
  });

  const handleFileChange = (type: "cv" | "cover") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (type === "cv") setCv(file);
    else setCover(file);
  };

  const handleDrop = (type: "cv" | "cover") => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files?.[0] || null;
    if (type === "cv") setCv(file);
    else setCover(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cv) { setError("Please upload your CV before submitting."); return; }
    setSending(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append("type",         "application");
      fd.append("role",         role?.title    || "General Application");
      fd.append("company",      role?.company  || "4REVA GROUP");
      fd.append("firstName",    form.firstName);
      fd.append("lastName",     form.lastName);
      fd.append("email",        form.email);
      fd.append("phone",        form.phone);
      fd.append("linkedin",     form.linkedin);
      fd.append("portfolio",    form.portfolio);
      fd.append("experience",   form.experience);
      fd.append("whyUs",        form.whyUs);
      fd.append("salary",       form.salary);
      fd.append("startDate",    form.startDate);
      fd.append("noticePeriod", form.noticePeriod);
      fd.append("cv",           cv);
      if (cover) fd.append("coverLetter", cover);

      const res = await fetch(API, { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      setSent(true);
    } catch (err: any) {
      setError(err.message || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  /* ── Success screen ── */
  if (sent) {
    return (
      <div style={{ minHeight: "100vh", background: C.offWhite, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          style={{ background: C.white, maxWidth: 540, width: "100%", padding: "60px 48px", textAlign: "center", borderTop: `4px solid ${C.red}` }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring" }}
            style={{ width: 72, height: 72, background: C.red, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", color: C.white }}>
            <CheckCircle size={36} strokeWidth={2} />
          </motion.div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, fontWeight: 400, color: C.black, margin: "0 0 16px" }}>Application Submitted</h2>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: C.gray, margin: "0 0 12px" }}>
            Thank you, <strong style={{ color: C.black }}>{form.firstName}</strong>. We've received your application for
          </p>
          <div style={{ display: "inline-block", background: C.redLt, border: `1px solid ${C.red}20`, padding: "8px 20px", marginBottom: 28 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.red, letterSpacing: "0.08em" }}>{role?.title || "General Application"}</span>
          </div>
          <p style={{ fontSize: 13, color: C.gray, lineHeight: 1.7, margin: "0 0 36px" }}>
            Our team will review your application and reach out within 5–7 business days.
          </p>
          <motion.button whileHover={{ scale: 1.02 }} onClick={() => navigate("/careers")}
            style={{ background: C.red, color: C.white, border: "none", padding: "14px 40px", fontWeight: 700, fontSize: 13, letterSpacing: "0.14em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            BACK TO CAREERS
          </motion.button>
        </motion.div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <>
      <Helmet>
        <title>{role?.title ? `Apply — ${role.title}` : "Apply — 4REVA GROUP"}</title>
      </Helmet>

      <div style={{ minHeight: "100vh", background: C.offWhite, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── NAV ── */}
        <nav style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "0 6%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          <motion.div whileHover={{ scale: 1.02 }} onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 12, color: C.white }}>SG</div>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 15, letterSpacing: "0.2em", color: C.black }}>4REVA GROUP</span>
          </motion.div>
          <button onClick={() => navigate("/careers")}
            style={{ background: "none", border: `1px solid ${C.border}`, color: C.gray, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", padding: "8px 18px", letterSpacing: "0.06em", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.black; e.currentTarget.style.color = C.black; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.gray; }}>
            ← Back to Careers
          </button>
        </nav>

        <div style={{ maxWidth: 760, margin: "0 auto", padding: "60px 24px 100px" }}>

          {/* ── Header ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ marginBottom: 48 }}>
            <p style={{ fontSize: 11, letterSpacing: "0.4em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 12px" }}>
              {role?.company || "4REVA GROUP"}
            </p>
            <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 400, color: C.black, margin: "0 0 16px", lineHeight: 1.1 }}>
              {role?.title || "General Application"}
            </h1>
            {role && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[role.type, role.location].map((t: string) => (
                  <span key={t} style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", color: C.gray, background: C.cream, padding: "4px 14px", textTransform: "uppercase" }}>{t}</span>
                ))}
              </div>
            )}
          </motion.div>

          <form onSubmit={handleSubmit}>

            {/* ── Personal Info ── */}
            <Section title="Personal Information" index={0}>
              <Grid>
                <Field label="First Name" required>
                  <input required value={form.firstName} onChange={set("firstName")}
                    onFocus={() => setFocus("firstName")} onBlur={() => setFocus(null)}
                    placeholder="First name" style={inp("firstName")} />
                </Field>
                <Field label="Last Name" required>
                  <input required value={form.lastName} onChange={set("lastName")}
                    onFocus={() => setFocus("lastName")} onBlur={() => setFocus(null)}
                    placeholder="Last name" style={inp("lastName")} />
                </Field>
              </Grid>
              <Grid>
                <Field label="Email Address" required>
                  <input required type="email" value={form.email} onChange={set("email")}
                    onFocus={() => setFocus("email")} onBlur={() => setFocus(null)}
                    placeholder="you@example.com" style={inp("email")} />
                </Field>
                <Field label="Phone Number">
                  <input type="tel" value={form.phone} onChange={set("phone")}
                    onFocus={() => setFocus("phone")} onBlur={() => setFocus(null)}
                    placeholder="+372 000 0000" style={inp("phone")} />
                </Field>
              </Grid>
              <Grid>
                <Field label="LinkedIn Profile">
                  <input value={form.linkedin} onChange={set("linkedin")}
                    onFocus={() => setFocus("linkedin")} onBlur={() => setFocus(null)}
                    placeholder="linkedin.com/in/yourname" style={inp("linkedin")} />
                </Field>
                <Field label="Portfolio / Website">
                  <input value={form.portfolio} onChange={set("portfolio")}
                    onFocus={() => setFocus("portfolio")} onBlur={() => setFocus(null)}
                    placeholder="yoursite.com" style={inp("portfolio")} />
                </Field>
              </Grid>
            </Section>

            {/* ── Experience ── */}
            <Section title="Experience" index={1}>
              <Field label="Years of Relevant Experience" required>
                <select required value={form.experience} onChange={set("experience")}
                  onFocus={() => setFocus("experience")} onBlur={() => setFocus(null)}
                  style={{ ...inp("experience"), appearance: "none" as const, cursor: "pointer" }}>
                  <option value="">Select experience level</option>
                  <option value="0-1">0–1 years</option>
                  <option value="1-3">1–3 years</option>
                  <option value="3-5">3–5 years</option>
                  <option value="5-8">5–8 years</option>
                  <option value="8+">8+ years</option>
                </select>
              </Field>
              <Field label="Why do you want to join 4REVA GROUP?" required>
                <textarea required value={form.whyUs} onChange={set("whyUs")}
                  onFocus={() => setFocus("whyUs")} onBlur={() => setFocus(null)}
                  placeholder="Tell us what excites you about this role and our mission..."
                  rows={5} style={{ ...inp("whyUs"), resize: "vertical" as const }} />
              </Field>
            </Section>

            {/* ── Logistics ── */}
            <Section title="Availability" index={2}>
              <Grid>
                <Field label="Expected Start Date">
                  <input type="date" value={form.startDate} onChange={set("startDate")}
                    onFocus={() => setFocus("startDate")} onBlur={() => setFocus(null)}
                    style={inp("startDate")} />
                </Field>
                <Field label="Notice Period">
                  <select value={form.noticePeriod} onChange={set("noticePeriod")}
                    onFocus={() => setFocus("notice")} onBlur={() => setFocus(null)}
                    style={{ ...inp("notice"), appearance: "none" as const, cursor: "pointer" }}>
                    <option value="">Select notice period</option>
                    <option value="immediate">Immediately available</option>
                    <option value="2weeks">2 weeks</option>
                    <option value="1month">1 month</option>
                    <option value="2months">2 months</option>
                    <option value="3months">3+ months</option>
                  </select>
                </Field>
              </Grid>
              <Field label="Salary Expectation (Annual, EUR)">
                <input value={form.salary} onChange={set("salary")}
                  onFocus={() => setFocus("salary")} onBlur={() => setFocus(null)}
                  placeholder="e.g. 60,000" style={inp("salary")} />
              </Field>
            </Section>

            {/* ── Documents ── */}
            <Section title="Documents" index={3}>
              {/* CV Upload */}
              <Field label="CV / Resume" required>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver("cv"); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={handleDrop("cv")}
                  onClick={() => cvRef.current?.click()}
                  style={{
                    border: `2px dashed ${dragOver === "cv" ? C.red : cv ? C.sage : C.border}`,
                    background: dragOver === "cv" ? C.redLt : cv ? C.sageLt : C.offWhite,
                    padding: "32px 24px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}>
                  <input ref={cvRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange("cv")} style={{ display: "none" }} />
                  {cv ? (
                    <div>
                      <FileText size={28} color={C.sage} style={{ margin: "0 auto 8px" }} />
                      <p style={{ fontSize: 14, fontWeight: 600, color: C.sage, margin: "0 0 4px" }}>{cv.name}</p>
                      <p style={{ fontSize: 12, color: C.gray, margin: 0 }}>{(cv.size / 1024).toFixed(0)} KB · Click to replace</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={28} color={C.gray} style={{ margin: "0 auto 12px" }} />
                      <p style={{ fontSize: 14, fontWeight: 600, color: C.black, margin: "0 0 6px" }}>Drop your CV here or click to browse</p>
                      <p style={{ fontSize: 12, color: C.gray, margin: 0 }}>PDF, DOC, DOCX · Max 10MB</p>
                    </div>
                  )}
                </div>
              </Field>

              {/* Cover Letter Upload */}
              <Field label="Cover Letter (Optional)">
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver("cover"); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={handleDrop("cover")}
                  onClick={() => coverRef.current?.click()}
                  style={{
                    border: `2px dashed ${dragOver === "cover" ? C.red : cover ? C.sage : C.border}`,
                    background: dragOver === "cover" ? C.redLt : cover ? C.sageLt : C.offWhite,
                    padding: "28px 24px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}>
                  <input ref={coverRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange("cover")} style={{ display: "none" }} />
                  {cover ? (
                    <div>
                      <FileText size={24} color={C.sage} style={{ margin: "0 auto 8px" }} />
                      <p style={{ fontSize: 13, fontWeight: 600, color: C.sage, margin: "0 0 4px" }}>{cover.name}</p>
                      <p style={{ fontSize: 11, color: C.gray, margin: 0 }}>Click to replace</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={24} color={C.gray} style={{ margin: "0 auto 10px" }} />
                      <p style={{ fontSize: 13, fontWeight: 600, color: C.black, margin: "0 0 4px" }}>Attach a cover letter</p>
                      <p style={{ fontSize: 11, color: C.gray, margin: 0 }}>PDF, DOC, DOCX · Optional but recommended</p>
                    </div>
                  )}
                </div>
              </Field>
            </Section>

            {/* ── Error ── */}
            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  style={{ padding: "14px 18px", background: C.redLt, border: `1px solid ${C.red}30`, fontSize: 13, color: C.red, marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
                  <AlertCircle size={16} />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Submit ── */}
            <motion.button type="submit" disabled={sending} whileHover={{ scale: sending ? 1 : 1.01 }} whileTap={{ scale: sending ? 1 : 0.98 }}
              style={{ width: "100%", background: sending ? C.grayLt : C.red, color: sending ? C.gray : C.white, border: "none", padding: "18px 0", fontWeight: 700, fontSize: 14, letterSpacing: "0.16em", cursor: sending ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" }}>
              {sending ? "SUBMITTING APPLICATION…" : "SUBMIT APPLICATION"}
            </motion.button>

            <p style={{ fontSize: 12, color: C.gray, textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
              By submitting, you agree to 4REVA GROUP processing your personal data for recruitment purposes.
            </p>
          </form>
        </div>
      </div>
    </>
  );
}

/* ─── Section wrapper ─── */
function Section({ title, children, index }: { title: string; children: React.ReactNode; index: number }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1, duration: 0.4 }}
      style={{ background: C.white, padding: "36px 36px", marginBottom: 4, borderTop: index === 0 ? `3px solid ${C.red}` : `1px solid ${C.border}` }}>
      <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 24px" }}>{title}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
    </motion.div>
  );
}

/* ─── Field wrapper ─── */
function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#444", marginBottom: 8, textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: C.red, marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

/* ─── 2-col grid ─── */
function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
      {children}
    </div>
  );
}

const C2 = C; // alias for use inside sub-components